import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "../App";
import { toast } from "sonner";
import { ShoppingBag, Plus, Trash2, Edit3, X, ImagePlus, Eye, EyeOff, ExternalLink } from "lucide-react";

const imgUrl = (u) => (u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);
const emptyForm = () => ({ name: "", price: "", link: "", images: [] });

export default function AdminShop() {
  const [products, setProducts] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/shop/products`)
      .then((res) => { setProducts(res.data.products); setEnabled(res.data.enabled); })
      .catch(() => toast.error("Errore nel caricamento dello shop"))
      .finally(() => setLoading(false));
  }, []);

  const toggleEnabled = async () => {
    const next = !enabled;
    try {
      await axios.put(`${API}/settings/shop_enabled`, { value: next ? "true" : "false" });
      setEnabled(next);
      toast.success(next ? "Shop VISIBILE agli utenti" : "Shop in Coming Soon per gli utenti");
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const uploadImages = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/upload/image`, fd);
        urls.push(res.data.url);
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      toast.success(`${urls.length} immagine/i caricate`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const saveProduct = async () => {
    if (!form.name.trim() || !form.link.trim()) { toast.error("Nome e link sono obbligatori"); return; }
    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.put(`${API}/shop/products/${editingId}`, form);
        setProducts((prev) => prev.map((p) => (p.id === editingId ? res.data : p)));
        toast.success("Prodotto aggiornato");
      } else {
        const res = await axios.post(`${API}/shop/products`, form);
        setProducts((prev) => [res.data, ...prev]);
        toast.success("Prodotto creato");
      }
      setForm(emptyForm());
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setForm({ name: p.name, price: p.price, link: p.link, images: p.images || [] });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProduct = async (pid) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    try {
      await axios.delete(`${API}/shop/products/${pid}`);
      setProducts((prev) => prev.filter((p) => p.id !== pid));
      toast.success("Prodotto eliminato");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <div className="page-container" data-testid="admin-shop-page">
      <PageHero image={HERO_IMAGES.strumenti} kicker="Admin" title="Gestione Shop" subtitle="Carica i prodotti e decidi quando aprire lo shop agli utenti" />
      <SectionBg type="crypto" />
      <div className="container py-8 relative">
        <div className="max-w-5xl mx-auto">
          {/* Toggle visibilità */}
          <div className="glass-card p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="shop-toggle-card">
            <div>
              <h2 className="text-base font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>Visibilità Shop</h2>
              <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>
                {enabled ? "Lo shop è attivo e visibile a tutti gli utenti." : "Gli utenti vedono il popup \"Coming Soon\". Tu puoi preparare tutto con calma."}
              </p>
            </div>
            <button onClick={toggleEnabled}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold uppercase transition-colors"
              style={{
                border: `1px solid ${enabled ? "#2FD8A8" : "#F59E0B"}`,
                color: enabled ? "#2FD8A8" : "#F59E0B",
                backgroundColor: enabled ? "rgba(47,216,168,0.1)" : "rgba(245,158,11,0.1)",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
              data-testid="shop-enabled-toggle">
              {enabled ? <><Eye size={15} /> Visibile</> : <><EyeOff size={15} /> Coming Soon</>}
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <Link to="/shop" className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--mm-gold)" }} data-testid="preview-shop-link">
              <ShoppingBag size={15} /> Anteprima Shop
            </Link>
            <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyForm()); }}
              className="btn-primary flex items-center gap-2 text-sm" data-testid="new-product-btn">
              <Plus size={16} /> Nuovo Prodotto
            </button>
          </div>

          {showForm && (
            <div className="glass-card p-6 sm:p-8 mb-8" data-testid="product-form">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>
                  {editingId ? "Modifica Prodotto" : "Nuovo Prodotto"}
                </h3>
                <button onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyForm()); }} className="p-1.5" style={{ border: "1px solid var(--mm-border)" }} data-testid="close-product-form">
                  <X size={15} style={{ color: "var(--mm-text-muted)" }} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Nome prodotto *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Felpa GoldVision" className="admin-input" data-testid="product-name-input" />
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Costo</label>
                  <input type="text" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="es. 49,90 €" className="admin-input" data-testid="product-price-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Link acquisto (shop esterno) *</label>
                  <input type="text" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." className="admin-input" data-testid="product-link-input" />
                </div>
              </div>
              <div className="mb-5">
                <label className="text-xs uppercase font-semibold block mb-2" style={{ color: "var(--mm-text-muted)" }}>Foto prodotto (anche più di una, es. i colori)</label>
                <div className="flex flex-wrap gap-3">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative" data-testid={`form-image-${i}`}>
                      <img src={imgUrl(img)} alt="" className="w-20 h-20 object-cover" style={{ border: "1px solid var(--mm-border)" }} />
                      <button onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })}
                        className="absolute -top-2 -right-2 p-0.5" style={{ backgroundColor: "#EF4444" }} data-testid={`remove-image-${i}`} aria-label="Rimuovi foto">
                        <X size={12} style={{ color: "#fff" }} />
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-20 flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-white/5"
                    style={{ border: "1px dashed var(--mm-border)" }} data-testid="upload-images-label">
                    <ImagePlus size={18} style={{ color: "var(--mm-gold)" }} />
                    <span className="text-[10px] mt-1" style={{ color: "var(--mm-text-muted)" }}>{uploading ? "Carico..." : "Aggiungi"}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={uploading}
                      onChange={(e) => e.target.files.length && uploadImages(e.target.files)} data-testid="upload-images-input" />
                  </label>
                </div>
              </div>
              <button onClick={saveProduct} disabled={saving || uploading} className="btn-primary text-sm" data-testid="save-product-btn">
                {saving ? "Salvo..." : editingId ? "Salva Modifiche" : "Crea Prodotto"}
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
            </div>
          ) : products.length === 0 ? (
            <div className="glass-card p-10 text-center" data-testid="admin-shop-empty">
              <p style={{ color: "var(--mm-text-muted)" }}>Nessun prodotto ancora. Crea il primo!</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-products-list">
              {products.map((p) => (
                <div key={p.id} className="glass-card p-4 flex flex-wrap items-center gap-4" data-testid={`admin-product-${p.id}`}>
                  {p.images?.[0] ? (
                    <img src={imgUrl(p.images[0])} alt="" className="w-16 h-16 object-cover flex-shrink-0" style={{ border: "1px solid var(--mm-border)" }} />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center flex-shrink-0" style={{ border: "1px solid var(--mm-border)" }}>
                      <ShoppingBag size={20} style={{ color: "var(--mm-border)" }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-[150px]">
                    <p className="text-sm font-bold" style={{ color: "var(--mm-text-primary)" }}>{p.name}</p>
                    <p className="text-sm gold-text font-semibold">{p.price}</p>
                    <a href={p.link} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "var(--mm-text-muted)" }}>
                      <ExternalLink size={11} /> {p.link.slice(0, 50)}{p.link.length > 50 ? "..." : ""}
                    </a>
                  </div>
                  <span className="text-xs flex-shrink-0" style={{ color: "var(--mm-text-muted)" }}>{(p.images || []).length} foto</span>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => startEdit(p)} className="p-2 transition-colors hover:bg-white/5" style={{ border: "1px solid var(--mm-border)" }} data-testid={`edit-product-${p.id}`} aria-label="Modifica">
                      <Edit3 size={14} style={{ color: "var(--mm-gold)" }} />
                    </button>
                    <button onClick={() => deleteProduct(p.id)} className="p-2 transition-colors hover:bg-red-500/10" style={{ border: "1px solid rgba(239,68,68,0.35)" }} data-testid={`delete-product-${p.id}`} aria-label="Elimina">
                      <Trash2 size={14} style={{ color: "#EF4444" }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
